/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unsafe-argument -- pcg-core surface is loose-typed by design */

// JSX rewrite of the prototype's render function: the Reject File Gate details panel
// (core-viewer.js rejectGateDetailsHtml, 3328-3389) as JSX. This is the gate
// metrics/reasons display for gate-limited (and rejected) recordings — the
// pre-report input-validity screen, shown for engineering/HCP audit review.
// All copy and fallback defaults are verbatim; escapeHtml is gone (React
// escapes).

import React, { ReactElement, ReactNode } from "react"
import { fmtNumber } from "../core/format"
import { rejectGate, rejectGateMetrics, gateClinicalSummary } from "../core/gate"
import { GuidePayload } from "../core"

const GateCell = ({ label, value }: { label: string; value: ReactNode }): ReactElement => (
  <div className="reject-gate-cell">
    <div className="reject-gate-label">{label}</div>
    <div className="reject-gate-value">{value}</div>
  </div>
)

const PcgRejectGatePanel = ({ payload }: { payload: GuidePayload }): ReactElement => {
  const gate = rejectGate(payload)
  if (!gate) {
    return (
      <div className="reject-gate-panel">
        <strong>Reject File Gate</strong>
        <div className="reject-gate-note">
          No Reject File Gate payload is attached to this recording. Use the server-backed analyzer for input-validity
          gating.
        </div>
      </div>
    )
  }
  const metrics = rejectGateMetrics(payload)
  const clinical =
    gateClinicalSummary(gate) || ({} as { decision?: string; analyzability?: string; inclusion?: string })
  const profilePayload = gate.input_profile || {}
  const conf = gate.confidence_decomposition || {}
  const downstream = gate.downstream_config || {}
  const status = gate.status || gate.action || "unknown"
  const analysisContext =
    profilePayload.profile === "medaica_standard_exam"
      ? "standard workflow"
      : String(profilePayload.profile || "").startsWith("public_benchmark_")
      ? "research benchmark"
      : "external/unknown profile"
  const profile = metrics.reject_gate_profile || "production"
  const minDuration = metrics.minimum_reportable_duration_s ?? 10
  const preferredDuration = metrics.preferred_duration_s ?? 20
  const strongPass = metrics.short_file_strong_pass_score ?? 0.8
  const overrideRequired = metrics.override_requires_rationale === false ? "No" : "Yes"
  const score = Number.isFinite(Number(gate.score)) ? `${fmtNumber(Number(gate.score) * 100, 0)}%` : "-"
  const message = gate.clinician_message || "Input-validity gate result for this file."
  const reason = gate.reason || "No additional gate reason."
  return (
    <div className="reject-gate-panel">
      <strong>Reject File Gate</strong>
      <div className="reject-gate-note">Pre-report input-validity screen only.</div>
      <div className="reject-gate-note">
        <strong>Analysis context</strong>
      </div>
      <div className="reject-gate-grid">
        <GateCell label="Gate decision" value={clinical.decision || "-"} />
        <GateCell label="Analyzability" value={clinical.analyzability || "-"} />
        <GateCell label="Review state" value={clinical.inclusion || "-"} />
        <GateCell label="Context" value={analysisContext} />
        <GateCell label="Profile" value={profilePayload.profile || "-"} />
        <GateCell label="Model fallback" value={downstream.morphology_model_fallback ? "Yes" : "No"} />
      </div>
      <div className="reject-gate-note">
        All report output requires HCP review. Profile/context fields are audit guides only and should not be
        interpreted as a separate patient-facing result.
      </div>
      <div className="reject-gate-note">
        <strong>Confidence</strong>
      </div>
      <div className="reject-gate-grid">
        <GateCell label="Signal" value={conf.signal_confidence || "-"} />
        <GateCell label="Segmentation" value={conf.segmentation_confidence || "-"} />
        <GateCell label="Model fit" value={conf.model_fit_confidence || "-"} />
        <GateCell label="Provenance" value={conf.provenance_confidence || "-"} />
      </div>
      <div className="reject-gate-note">
        <strong>Current file outcome</strong>
      </div>
      <div className="reject-gate-grid">
        <GateCell label="Gate outcome" value={status} />
        <GateCell label="Input-validity score" value={score} />
        <GateCell label="Action" value={gate.action || "-"} />
      </div>
      <div className="reject-gate-note">{message}</div>
      <div className="reject-gate-note">Reason: {reason}</div>
      <div className="reject-gate-note">
        <strong>Reject File config settings</strong>
      </div>
      <div className="reject-gate-grid">
        <GateCell label="Config profile" value={profile} />
        <GateCell label="Minimum duration" value={`${minDuration}s`} />
        <GateCell label="Preferred duration" value={`${preferredDuration}s`} />
        <GateCell label="Short-file strong pass" value={`${fmtNumber(Number(strongPass) * 100, 0)}%`} />
        <GateCell label="Override rationale" value={overrideRequired} />
      </div>
      <div className="reject-gate-note">
        Config settings are shown for engineering/Admin review and should be persisted by profile in production.
        Rejected files should not generate reports unless an HCP/admin override is logged with rationale.
      </div>
    </div>
  )
}

export default PcgRejectGatePanel
